import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';

import type { TreatmentApproach } from '@/entities/session';
import { type GenerateNote, generateNoteSchema } from '@/entities/session-note';

import { EditTreatmentApproachesContentDialogForm } from './edit-treatment-approaches-content/edit-treatment-approaches-content-dialog-form';
import { EditTreatmentApproachesContentPopoverForm } from './edit-treatment-approaches-content/edit-treatment-approaches-content-popover-form';

type Props = {
    selectedApproaches: TreatmentApproach[];
    isOpened: boolean;
    variant?: 'popover' | 'dialog';
    onSubmit: (data: TreatmentApproach[]) => void;
    onCancel: () => void;
};

export function EditTreatmentApproachesContent({
    selectedApproaches,
    isOpened,
    variant = 'popover',
    onSubmit,
    onCancel,
}: Props) {
    const form = useForm<GenerateNote>({
        mode: 'onChange',
        resolver: zodResolver(generateNoteSchema),
        defaultValues: {
            treatmentApproaches: selectedApproaches,
        },
    });

    useEffect(() => {
        if (isOpened) {
            form.reset({ treatmentApproaches: selectedApproaches });
        }
    }, [isOpened]);

    const handleSubmit = (data: GenerateNote) => {
        onSubmit(data.treatmentApproaches);
    };

    const handleCancel = () => {
        form.reset({ treatmentApproaches: selectedApproaches });
        onCancel();
    };

    return (
        <FormProvider {...form}>
            {variant === 'dialog' ? (
                <EditTreatmentApproachesContentDialogForm
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                />
            ) : (
                <EditTreatmentApproachesContentPopoverForm
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                />
            )}
        </FormProvider>
    );
}
